/**
 * Analytics tracking utility
 * Tracks user interactions, events, and conversions
 */

declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
  }
}

// Google Ads Conversion ID
const GOOGLE_ADS_ID = 'AW-17507546018';
const CONVERSION_LABEL = 'BJ92CLW4wY0bEKLnn5xB';

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (import.meta.env.DEV) {
    console.log('[Analytics]', eventName, properties);
    if (eventName === 'button_click') {
      console.log(`[Button Click] ${properties?.label || 'Unknown'} - Subject: ${properties?.subject || 'N/A'}`);
    }
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, properties);
  }
};

export const trackPageView = (pageName: string) => {
  trackEvent('page_view', { page_name: pageName });
};

export const trackButtonClick = (buttonLabel: string, emailSubject?: string) => {
  trackEvent('button_click', { 
    label: buttonLabel,
    subject: emailSubject,
    timestamp: new Date().toISOString()
  });
};

export const trackAIAnalysis = (stepTitle: string, success: boolean) => {
  trackEvent('ai_analysis', {
    step_title: stepTitle,
    success,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track Google Ads conversion for form submissions
 */
export const trackFormSubmission = (formType: string, value: number = 1) => {
  if (import.meta.env.DEV) console.log('[Google Ads] Form submission conversion:', formType);
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}`,
      value: value,
      currency: 'USD',
      transaction_id: `${formType}_${Date.now()}`,
    });
  }
  
  // Also track as a GA4 event
  trackEvent('form_submission', {
    form_type: formType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track Google Ads conversion for demo bookings
 */
export const trackDemoBooking = () => {
  if (import.meta.env.DEV) console.log('[Google Ads] Demo booking conversion');
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}`,
      value: 10,
      currency: 'USD',
      transaction_id: `demo_${Date.now()}`,
    });
  }
  
  // Also track as a GA4 event
  trackEvent('demo_booking', {
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track newsletter signup
 */
export const trackNewsletterSignup = () => {
  if (import.meta.env.DEV) console.log('[Google Ads] Newsletter signup conversion');
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}`,
      value: 1,
      currency: 'USD',
      transaction_id: `newsletter_${Date.now()}`,
    });
  }
  
  trackEvent('newsletter_signup', {
    timestamp: new Date().toISOString(),
  });
};
